import type { Query } from '@tanstack/react-query'
import {
  getErrorStatus,
  isAuthenticationAvailabilityError,
  isLostResponseError,
} from '@/lib/api/error-utils'
import { InvalidJsonResponseError } from '@/lib/api/json-response'
import { getMutationErrorMessage } from '@/lib/api/mutation-feedback'
import { isWorkspaceReadOnlyError } from '@/lib/billing/workspace-read-only'
import { createLogger } from '@/lib/logger'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { InvalidApiResponseError } from '@scrambl/shared/api-errors'
import { SessionChangedError } from '@scrambl/shared/session-scope'

const logger = createLogger('lib/queryClient')

const RETRY_DELAYS = [1000, 3000, 5000]

/**
 * A refused request: the session is gone (401) or this user is not allowed to
 * read the resource (403). Neither changes on retry.
 */
export function isAuthError(error: unknown): boolean {
  const status = getErrorStatus(error)
  if (status === 401 || status === 403) return true
  if (status) return false
  return (
    error instanceof Error &&
    /401|403|unauthorized|forbidden/i.test(error.message)
  )
}

/**
 * The request never got an answer from the API (fetch rejected, or a gateway
 * or deadline stood in for it). Unlike a 4xx or a plain 500, this says nothing
 * about the resource; it only says the server could not be reached.
 */
export function isConnectivityError(error: unknown): boolean {
  // A received but malformed body is not an outage; unrelated successes must not refetch it.
  if (
    error instanceof InvalidJsonResponseError ||
    error instanceof InvalidApiResponseError
  )
    return false
  if (isLostResponseError(error)) return true
  const status = getErrorStatus(error)
  return status === 408 || status === 502 || status === 503 || status === 504
}

/**
 * A query that is re-tried on recovery waits at least this long before it can
 * be re-tried that way again, so an endpoint that keeps failing is not
 * re-requested on every unrelated success.
 */
export const RECOVERY_REFETCH_COOLDOWN_MS = 10_000

export function createQueryCache(): QueryCache {
  const lastRecoveryRefetchAt = new WeakMap<Query, number>()

  // Once any request succeeds the API is reachable again, so every mounted
  // query that gave up during the outage is fetched again. Without this a
  // failed lookup sits in the error state until a window focus or an
  // offline→online transition, neither of which a server restart produces.
  // Gate lookups (record permissions, flags, subscription features) resolve to
  // "denied" while they have no data, so the surfaces they hide stayed hidden
  // until a full reload even after the page's own "Try again" had recovered.
  const refetchQueriesFailedByOutage = (cache: QueryCache, now: number) => {
    for (const query of cache.getAll()) {
      if (
        query.state.status !== 'error' ||
        query.state.fetchStatus !== 'idle' ||
        !query.isActive() ||
        !isConnectivityError(query.state.error)
      ) {
        continue
      }
      const lastAttempt = lastRecoveryRefetchAt.get(query) ?? 0
      if (now - lastAttempt < RECOVERY_REFETCH_COOLDOWN_MS) continue
      lastRecoveryRefetchAt.set(query, now)
      void query.fetch().catch(() => undefined)
    }
  }

  const cache: QueryCache = new QueryCache({
    onSuccess: () => {
      refetchQueriesFailedByOutage(cache, Date.now())
    },
    onError: (error, query) => {
      if (import.meta.env.DEV) {
        logger.error('[ReactQuery] Query error', {
          key: query.queryKey,
          error,
        })
      }
      // React Query keeps the previous `data` when a refetch fails, so a list
      // that was cached (in memory or restored from the persisted snapshot)
      // and then refused would keep rendering rows over the error. A 401/403/404
      // means this user must not see that data at all: drop it, keep the
      // error. The Query object stays in the cache so mounted observers see
      // the error state instead of rebuilding a fresh query and refetching
      // in a loop, and the persister's success-only filter keeps it out of
      // storage.
      if (
        (isAuthError(error) || getErrorStatus(error) === 404) &&
        query.state.data !== undefined
      ) {
        query.setState({ data: undefined, dataUpdatedAt: 0 })
      }
    },
  })
  return cache
}

export function createMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (error instanceof SessionChangedError) return
      if (mutation.meta?.silent) return
      // The api client already showed the read-only toast for this write.
      if (isWorkspaceReadOnlyError(error)) return
      if (import.meta.env.DEV) {
        logger.error('[ReactQuery] Mutation error', {
          status: getErrorStatus(error),
          kind: isLostResponseError(error) ? 'unconfirmed' : 'rejected',
        })
      }
      toast.error(
        getMutationErrorMessage(
          error,
          (mutation.meta?.errorMessage as string | undefined) ??
            'Something went wrong. Please try again.',
        ),
      )
    },
  })
}

export const queryClient = new QueryClient({
  queryCache: createQueryCache(),
  mutationCache: createMutationCache(),
  defaultOptions: {
    queries: {
      // For real-time apps: shorter stale time since WebSocket updates keep data fresh
      // Data becomes stale after 30 seconds, triggering background refetch on next access
      staleTime: 30 * 1000,
      // Retain inactive data long enough for fast in-app revisits while avoiding
      // the previous 24-hour accumulation. This remains above the 5-minute
      // persisted-cache restore window.
      gcTime: 1000 * 60 * 30,
      retry: (failureCount, error) => {
        if (error instanceof Error && error.name === 'AbortError') return false
        if (
          error instanceof InvalidJsonResponseError ||
          error instanceof InvalidApiResponseError
        )
          return false
        // 401/403 will not change on retry, and a 404 (missing record, or a
        // record the viewer cannot read) will not either — retrying only
        // leaves detail pages on a skeleton for ~9s before the not-found state.
        if (isAuthError(error)) {
          return false
        }
        const status = getErrorStatus(error)
        // The transport owns 429 retries; permanent client errors do not improve on retry.
        if (
          status !== undefined &&
          status >= 400 &&
          status < 500 &&
          status !== 408
        ) {
          return false
        }
        if (status === 503 && isAuthenticationAvailabilityError(error)) {
          return false
        }
        return failureCount < 3
      },
      retryDelay: attemptIndex =>
        RETRY_DELAYS[Math.min(attemptIndex, RETRY_DELAYS.length - 1)],
      // Refetch stale data when window regains focus (user returns to tab)
      refetchOnWindowFocus: true,
      // Refetch when network reconnects (important for real-time sync)
      refetchOnReconnect: true,
      // Refetch on mount whenever the data is stale (older than `staleTime`).
      // This is gated by `staleTime` (30s), so it is NOT "refetch on every
      // mount": a page revisited within 30s — or kept fresh by WebSocket
      // updates — costs zero refetches, while a page returned to after a long
      // time (e.g. after an org switch / client-portal round-trip) restores
      // itself with fresh data on navigation. `true` also covers explicitly
      // invalidated queries, since invalidation marks them stale — so the
      // previous behavior (cross-module updates, socket fallbacks) is a subset.
      // We rely on this rather than `query => query.state.isInvalidated`, which
      // missed the common case: data that went stale without a recorded
      // invalidation event (org switch early-returns, in-app navigation on
      // desktop where no window-focus fires) never refetched and stayed stale.
      refetchOnMount: true,
    },
    mutations: {
      retry: 0,
    },
  },
})
